<template>
  <div
    data-component="base-layout-card"
    :class="{
      'is-menu-open': isMenuOpen,
      'show-menu-button': showMenuButton,
      'can-navigate': canNavigateOnClick || canSelectOnClick,
      'show-warning': showWarning,
    }"
    @click="onItemClick"
  >
    <div
      v-if="isActive"
      class="base-layout-card-active-indicator"
    >
      {{ activeLayoutLabel }}
    </div>

    <div
      v-if="layout.madeByAgent"
      class="base-layout-card-local-indicator"
    >
      {{ $t('HITL.ui-customization.layout-card.local-layout') }}
    </div>

    <div
      class="base-layout-card-preview"
      :style="layoutPreviewGrid"
    >
      <div
        v-for="item in layout.items"
        :key="item.key"
        class="base-layout-card-preview-item"
        :style="getPreviewItemStyle(item)"
      />
    </div>

    <div class="base-layout-card-info">
      <div class="base-layout-card-info-header">
        <or-tooltip
          v-if="showBookmarkButton"
          v-model="isBookmarkTooltipVisible"
          :text="bookmarkTooltipText"
        >
          <or-icon-button
            class="base-layout-card-info-bookmark"
            data-button="base-layout-card-bookmark"
            :class="{ 'is-bookmarked': !!layout.bookmarkedAt }"
            :icon="bookmarkIcon"
            @click.stop="$emit('bookmark')"
          />
        </or-tooltip>

        <span
          class="base-layout-card-info-name"
          data-text="base-layout-card-info-name"
        >
          {{ $t(layout.name) }}
        </span>

        <or-icon-button
          v-if="showMenuButton"
          ref="button"
          data-button="base-layout-card-menu"
          icon="more_vert"
          @click.stop="isMenuOpen = true"
        />

        <or-menu
          v-if="showMenuButton"
          ref="menu"
          :trigger="buttonRoot"
          :is-open="isMenuOpen"
          data-menu="base-layout-card-menu"
          @open="isMenuOpen = true"
          @close="isMenuOpen = false"
        >
          <or-menu-item
            v-for="{ value, label, isDisabled } in menuOptions"
            :key="value"
            :data-menu-item="value"
            :disabled="isDisabled"
            @click.stop="onMenuItemClick(value, !!isDisabled)"
          >
            {{ label }}
          </or-menu-item>
        </or-menu>
      </div>

      <div
        v-if="layout.description"
        class="base-layout-card-info-description"
      >
        {{ layout.description }}
      </div>
    </div>

    <or-confirm
      ref="confirm"
      :is-open="isDeleteConfirmOpen"
      :title-text="$t('HITL.ui-customization.layout-card.delete-layout-title')"
      confirm-type="d"
      @confirm="onDeleteConfirm"
      @close="onDeleteCancel"
    >
      <template #default>
        <i18n
          slot="content"
          path="HITL.ui-customization.layout-card.delete-layout-description"
        >
          <strong slot="text">
            {{ tLayoutLabel }}
          </strong>
        </i18n>
      </template>
    </or-confirm>
  </div>
</template>

<script lang="ts">
import _ from 'lodash';
import Vue from 'vue';
import {
  defineComponent,
  PropType,
  toRefs,
  ref,
  computed,
  getCurrentInstance,
} from 'vue-demi';
import {
  OrMenuV3 as OrMenu,
  OrMenuItemV3 as OrMenuItem,
  OrTooltipV3 as OrTooltip,
  OrIconButtonV3 as OrIconButton,
  OrButtonV3 as OrButton,
  OrConfirmV3 as OrConfirm,
  IconVariant,
} from '@onereach/ui-components-25';
import { IFlexibleLayoutItem } from '@onereach/grid-layout';

import { Layout } from '@/types';
import { LayoutMenuAction } from '@/constants';

interface MenuOption {
  readonly value: LayoutMenuAction;
  readonly label: string;
  readonly isDisabled?: boolean;
}

const SKELETON_COLUMNS = 10;
const SKELETON_ROWS = 10;

export default defineComponent({
  name: 'BaseLayoutCard',
  components: {
    OrMenu,
    OrMenuItem,
    OrTooltip,
    OrIconButton,
    OrConfirm,
  },
  props: {
    layout: {
      type: Object as PropType<Layout>,
      default: () => ({}),
    },
    menuOptions: {
      type: Array as PropType<MenuOption[]>,
      default: () => [],
    },
    isActive: Boolean,
    showBookmarkButton: Boolean,
    showMenuButton: Boolean,
    showWarning: Boolean,
    canNavigateOnClick: Boolean,
    canSelectOnClick: Boolean,
  },
  emits: ['select', 'delete', 'bookmark', 'reset', 'navigate'],
  setup(props) {
    const {
      showBookmarkButton,
      canNavigateOnClick,
      canSelectOnClick,
      layout,
    } = toRefs(props);
    const vm = getCurrentInstance()!;

    const isDeleteConfirmOpen = ref(false);
    const isBookmarkTooltipVisible = ref(false);
    const isMenuOpen = ref(false);
    const button = ref<InstanceType<typeof OrButton>>();
    const buttonRoot = computed(() => button.value?.root);

    const bookmarkTooltipText = computed(() => {
      return layout.value.bookmarkedAt
        ? vm.proxy.$t('HITL.ui-customization.layout-card.actions.bookmark-tooltip-remove')
        : vm.proxy.$t('HITL.ui-customization.layout-card.actions.bookmark-tooltip-add');
    });

    const bookmarkIcon = computed(() => {
      return {
        icon: 'star',
        variant: layout.value.bookmarkedAt
          ? IconVariant.Filled
          : IconVariant.Outlined,
      };
    });

    const tLayoutLabel = computed(() => _.truncate(layout.value.name));
    const activeLayoutLabel = computed(() => {
      return showBookmarkButton.value
        ? vm.proxy.$t('HITL.ui-customization.layout-card.active-layout')
        : vm.proxy.$t('HITL.ui-customization.layout-card.default-layout');
    });

    const layoutPreviewGrid = computed(() => {
      return {
        gridTemplateColumns: `repeat(${SKELETON_COLUMNS}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${SKELETON_ROWS}, minmax(0, 1fr))`,
      };
    });

    const getGridAreaStyle = (args: {
      column: number;
      row: number;
      width: number;
      height: number;
    }) => {
      const column = args.column + 1;
      const row = args.row + 1;
      const width = `span ${args.width}`;
      const height = `span ${args.height}`;
      return {
        gridArea: `${row} / ${column} / ${height} / ${width}`,
      };
    };

    const getPreviewItemStyle = (item: IFlexibleLayoutItem) => {
      const row = Math.round((item.row / layout.value.rows) * SKELETON_ROWS);
      const column = Math.round((item.column / layout.value.columns) * SKELETON_COLUMNS);
      return getGridAreaStyle({
        row,
        column,
        width: Math.min(
          Math.round((item.width / layout.value.columns) * SKELETON_COLUMNS),
          SKELETON_COLUMNS - column,
        ),
        height: Math.min(
          Math.round((item.height / layout.value.rows) * SKELETON_ROWS),
          SKELETON_ROWS - row,
        ),
      });
    };

    const onDeleteModalOpen = () => (isDeleteConfirmOpen.value = true);
    const onDeleteCancel = () => (isDeleteConfirmOpen.value = false);
    const onDeleteConfirm = () => {
      isDeleteConfirmOpen.value = false;
      vm.proxy.$emit('delete');
    };

    const onMenuItemClick = (action: LayoutMenuAction, isDisabled: boolean) => {
      if (isDisabled) {
        return;
      }

      isMenuOpen.value = false;
      switch (action) {
        case LayoutMenuAction.SELECT:
        case LayoutMenuAction.BOOKMARK:
        case LayoutMenuAction.RESET:
          vm.proxy.$emit(action.toLowerCase());
          break;
        case LayoutMenuAction.DELETE:
          onDeleteModalOpen();
          break;
      }
    };

    const onItemClick = (event: MouseEvent) => {
      if (canSelectOnClick.value) {
        return vm.proxy.$emit('select');
      }

      if (!canNavigateOnClick.value) {
        return;
      }

      const menuRef = vm.proxy.$refs['menu'] as Vue;
      if (menuRef) {
        const menuElement = menuRef.$el as HTMLElement;
        if (menuElement.contains(event.target as Node)) {
          return;
        }
      }

      const confirmRef = vm.proxy.$refs['confirm'] as Vue;
      if (confirmRef) {
        const confirmElement = confirmRef.$el as HTMLElement;
        if (confirmElement.contains(event.target as Node)) {
          return;
        }
      }

      vm.proxy.$emit('navigate', { layout: layout.value.key });
    };

    return {
      LayoutMenuAction,
      isMenuOpen,
      button,
      buttonRoot,
      isBookmarkTooltipVisible,
      bookmarkTooltipText,
      bookmarkIcon,
      activeLayoutLabel,
      isDeleteConfirmOpen,
      layoutPreviewGrid,
      tLayoutLabel,
      getPreviewItemStyle,
      onMenuItemClick,
      onDeleteModalOpen,
      onDeleteCancel,
      onDeleteConfirm,
      onItemClick,
    };
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='base-layout-card'] {
  display: flex;
  flex-direction: column;
  position: relative;
  gap: var(--s-4);
  width: 16rem;
  height: 18.5rem;
  padding: var(--s-4);
  color: var(--or-c-on-background);
  border-radius: var(--s-2);
  background: var(--or-c-background);
  box-shadow: var(--or-shadow);

  &.can-navigate {
    cursor: pointer;
  }

  &.show-warning {
    outline: 1px solid var(--c-error);
  }

  .base-layout-card-active-indicator {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: var(--s-3) var(--s-6);
    background: var(--c-primary);
    color: var(--or-c-on-primary);
    border-radius: var(--s-2) var(--s-2) 0 0;

    &::before {
      content: '';
      position: absolute;
      left: var(--s-4);
      top: 0.85rem;
      width: 0.625rem;
      height: 0.625rem;
      background: var(--or-c-on-primary);
      border-radius: 50%;
    }
  }

  .base-layout-card-local-indicator {
    position: absolute;
    top: var(--s-5);
    right: var(--s-5);
    padding: var(--s-2) var(--s-3);
    border-radius: var(--s-6);
    background: var(--or-c-primary-container);
    font-size: var(--fs-0);
  }

  .base-layout-card-preview {
    flex: 0 0 auto;
    width: 100%;
    height: 8.5rem;
    display: grid;
    grid-gap: var(--s-3);

    &-item {
      border-radius: var(--s-2);
      background: var(--or-c-surface-1);
    }
  }

  .base-layout-card-info {
    display: flex;
    flex-direction: column;
    gap: var(--s-4);

    &-header {
      display: flex;
      align-items: center;
      gap: var(--s-2);

      .base-layout-card-info-name {
        @include line-clamp(1);
        font-weight: 700;
        font-size: var(--fs-1);
        color: var(--or-c-on-background);
      }

      [data-button='base-layout-card-bookmark'] {
        &.is-bookmarked {
          ::v-deep .or-icon-v3 {
            color: var(--or-c-warning) !important;
          }
        }
      }

      [data-button='base-layout-card-menu'] {
        margin-left: auto;
      }
    }

    &-description {
      @include line-clamp(3);
      font-size: var(--fs-0);
      line-height: var(--lh-0);
      color: var(--or-c-outline);
      margin-top: var(--s-2);
    }
  }
}
</style>
